/**
 * Programmatic BIMI SVG Converter
 * Converts SVG to BIMI-compliant format WITHOUT modifying visual content
 * ONLY changes: SVG attributes, viewBox (make square), add title/desc
 */

/**
 * Extract <title> and <desc> elements from SVG content so they can be hoisted
 * to the top of the document. SVG Tiny PS (BIMI profile) requires <title> to be
 * the FIRST child element of <svg>; DigiCert rejects logos where anything else
 * (e.g. a background <rect>) precedes it.
 * @param {string} content - SVG inner content (between <svg> and </svg>)
 * @returns {{title: string|null, desc: string|null, body: string, duplicatesRemoved: number}}
 */
export function hoistTitleAndDesc(content) {
  const titleRegex = /<title[^>]*>[\s\S]*?<\/title>|<title[^>]*\/>/gi;
  const descRegex = /<desc[^>]*>[\s\S]*?<\/desc>|<desc[^>]*\/>/gi;

  const titles = content.match(titleRegex) || [];
  const descs = content.match(descRegex) || [];

  let body = content;
  if (titles.length > 0) body = body.replace(titleRegex, '');
  if (descs.length > 0) body = body.replace(descRegex, '');

  return {
    title: titles[0] || null,
    desc: descs[0] || null,
    body,
    duplicatesRemoved: Math.max(0, titles.length - 1) + Math.max(0, descs.length - 1),
  };
}

/**
 * Get the tag name of the first child element in SVG content (ignoring comments).
 * @param {string} content - SVG inner content
 * @returns {string|null} Lowercase tag name or null
 */
function getFirstChildElementName(content) {
  const match = content.replace(/<!--[\s\S]*?-->/g, '').match(/<\s*([a-zA-Z][\w-]*)/);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Convert SVG to BIMI-compliant format programmatically
 * This function ONLY modifies the SVG wrapper - content is UNTOUCHED
 * @param {string} svgContent - Original SVG content
 * @returns {Object} Result with fixedSvg, fixesApplied, errors, warnings
 */
export function programmaticBimiConvert(svgContent) {
  const result = {
    fixedSvg: svgContent,
    fixesApplied: [],
    errors: [],
    warnings: [],
    isValid: false,
  };

  if (!svgContent || typeof svgContent !== 'string') {
    result.errors.push('Invalid SVG content provided');
    return result;
  }

  try {
    let svg = svgContent.trim();

    // Step 1: Ensure XML declaration
    let hasXmlDecl = svg.startsWith('<?xml');
    if (hasXmlDecl) {
      // Remove it - we'll add a clean one
      svg = svg.replace(/^<\?xml[^>]*\?>\s*/i, '').trim();
    }
    result.fixesApplied.push('Added XML declaration');

    // Step 2: Find the SVG tag
    const svgTagMatch = svg.match(/<svg([^>]*)>/i);
    if (!svgTagMatch) {
      result.errors.push('No valid <svg> tag found');
      return result;
    }

    const svgTagStart = svgTagMatch.index;
    const svgTagEnd = svgTagMatch.index + svgTagMatch[0].length;
    const originalAttrs = svgTagMatch[1];

    // Step 3: Find closing tag
    const closeMatch = svg.match(/<\/svg>\s*$/i);
    if (!closeMatch) {
      result.errors.push('No closing </svg> tag found');
      return result;
    }

    // Step 4: Extract the content between <svg> and </svg>
    let innerContent = svg.substring(svgTagEnd, closeMatch.index);

    // Step 4.5: Handle <style> elements - BIMI forbids them, must inline CSS
    const styleResult = inlineCssStyles(innerContent);
    if (styleResult.modified) {
      innerContent = styleResult.content;
      result.fixesApplied.push('Converted CSS classes to inline styles');
      if (styleResult.removedStyles > 0) {
        result.fixesApplied.push(`Removed ${styleResult.removedStyles} <style> element(s)`);
      }
    }

    // Step 4.6: Clean up non-compliant attributes (always run)
    const originalInnerContent = innerContent;

    // Remove data-* attributes (NOT allowed in SVG Tiny 1.2)
    innerContent = innerContent.replace(/\s+data-[a-zA-Z0-9-]+\s*=\s*["'][^"']*["']/gi, '');

    // Remove no-op transforms like transform="translate(0 0)"
    innerContent = innerContent.replace(
      /\s+transform\s*=\s*["']translate\s*\(\s*0\s*[,\s]\s*0\s*\)["']/gi,
      ''
    );

    if (innerContent !== originalInnerContent) {
      result.fixesApplied.push('Removed non-compliant attributes (data-*, no-op transforms)');
    }

    // Step 4.7: Handle gradients - linearGradient and radialGradient are NOT allowed in BIMI SVG P/S
    // We convert gradients to solid colors and warn the user about the change
    const gradientResult = replaceGradientsWithSolidColors(innerContent);
    if (gradientResult.modified) {
      innerContent = gradientResult.content;
      result.fixesApplied.push(
        'Converted gradients to solid colors (required for BIMI compliance)'
      );
      result.hasGradients = true;
      result.gradientsRemoved = gradientResult.gradientsReplaced;

      // Add clear warning about gradient removal
      result.warnings.push(
        `IMPORTANT: Your logo contained ${gradientResult.gradientsReplaced} gradient(s) which are NOT supported in BIMI SVG format. ` +
          `Gradients have been replaced with solid colors. The visual appearance has changed. ` +
          `If this is not acceptable, please redesign your logo without gradients.`
      );
    }

    // Step 4.8: Remove clipPath elements and clip-path attributes - NOT allowed in SVG Tiny 1.2 PS
    const clipPathResult = removeClipPaths(innerContent);
    if (clipPathResult.modified) {
      innerContent = clipPathResult.content;
      result.fixesApplied.push('Removed clipPath elements (not allowed in BIMI SVG Tiny PS)');
      if (clipPathResult.clipPathsRemoved > 0) {
        result.warnings.push(
          `Removed ${clipPathResult.clipPathsRemoved} clipPath element(s). Visual clipping has been removed. ` +
            `If this changes your logo appearance significantly, you may need to redesign it without clip paths.`
        );
      }
    }

    // Step 4.9: Remove other forbidden elements (mask, filter, pattern, use with external refs, etc.)
    const forbiddenResult = removeForbiddenElements(innerContent);
    if (forbiddenResult.modified) {
      innerContent = forbiddenResult.content;
      for (const fix of forbiddenResult.fixes) {
        result.fixesApplied.push(fix);
      }
      for (const warning of forbiddenResult.warnings) {
        result.warnings.push(warning);
      }
    }

    // Step 5: Parse viewBox to make it square
    let viewBox = parseViewBox(originalAttrs);
    let viewBoxStr;

    if (viewBox) {
      if (Math.abs(viewBox.width - viewBox.height) > 0.01) {
        // Make square by EXPANDING
        const maxDim = Math.max(viewBox.width, viewBox.height);
        const newX = viewBox.x - (maxDim - viewBox.width) / 2;
        const newY = viewBox.y - (maxDim - viewBox.height) / 2;
        viewBoxStr = `${newX} ${newY} ${maxDim} ${maxDim}`;
        result.fixesApplied.push(
          `Made viewBox square: ${viewBox.width}x${viewBox.height} → ${maxDim}x${maxDim}`
        );
      } else {
        viewBoxStr = `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`;
      }
    } else {
      // Try to get from width/height
      const widthMatch = originalAttrs.match(/\bwidth\s*=\s*["']([^"']+)["']/i);
      const heightMatch = originalAttrs.match(/\bheight\s*=\s*["']([^"']+)["']/i);

      if (widthMatch && heightMatch) {
        const w = parseFloat(widthMatch[1]);
        const h = parseFloat(heightMatch[1]);
        if (!isNaN(w) && !isNaN(h)) {
          const maxDim = Math.max(w, h);
          const newX = -(maxDim - w) / 2;
          const newY = -(maxDim - h) / 2;
          viewBoxStr = `${newX} ${newY} ${maxDim} ${maxDim}`;
          result.fixesApplied.push(
            `Created viewBox from width/height: ${w}x${h} → ${maxDim}x${maxDim}`
          );
        }
      }

      if (!viewBoxStr) {
        viewBoxStr = '0 0 100 100';
        result.warnings.push('No viewBox found - using default 100x100');
      }
    }

    // Step 6: Check what needs to be added
    const needsXmlns = !/xmlns\s*=\s*["']http:\/\/www\.w3\.org\/2000\/svg["']/i.test(originalAttrs);
    const needsBaseProfile = !/baseProfile\s*=\s*["']tiny-ps["']/i.test(originalAttrs);
    const needsVersion = !/version\s*=\s*["']1\.2["']/i.test(originalAttrs);

    if (needsXmlns) result.fixesApplied.push('Added xmlns attribute');
    if (needsBaseProfile) result.fixesApplied.push('Added baseProfile="tiny-ps"');
    if (needsVersion) result.fixesApplied.push('Added version="1.2"');

    // Step 7: Build new SVG tag with BIMI attributes
    // Preserve existing attributes that we need to keep
    const preservedAttrs = [];

    // CRITICAL: Preserve visual presentation attributes from SVG root
    // These affect ALL child elements (cascading defaults)
    const attributesToPreserve = [
      'fill', // CRITICAL: fill="none" on root is needed for stroke-only logos
      'stroke', // Default stroke color
      'stroke-width', // Default stroke width
      'stroke-linecap', // Stroke cap style
      'stroke-linejoin', // Stroke join style
      'stroke-miterlimit',
      'stroke-dasharray',
      'stroke-dashoffset',
      'stroke-opacity',
      'fill-opacity',
      'fill-rule',
      'opacity', // Overall opacity
      'color', // currentColor reference
      'id',
      // 'class', - REMOVED: class attributes not useful without <style>
      // 'style', - REMOVED: inline style on root not recommended for BIMI
      'xml:space',
    ];

    for (const attr of attributesToPreserve) {
      // Handle xml:space specially (colon in attribute name)
      const escapedAttr = attr.replace(':', '\\:');
      const regex = new RegExp(`\\b${escapedAttr}\\s*=\\s*["']([^"']+)["']`, 'i');
      const match = originalAttrs.match(regex);
      if (match) {
        preservedAttrs.push(`${attr}="${match[1]}"`);
      }
    }

    // Build the new SVG opening tag
    const newSvgAttrs = [
      'xmlns="http://www.w3.org/2000/svg"',
      `viewBox="${viewBoxStr}"`,
      'version="1.2"',
      'baseProfile="tiny-ps"',
      ...preservedAttrs,
    ].join(' ');

    // Step 8: Hoist <title>/<desc> to the top of the document.
    // SVG Tiny PS requires <title> as the FIRST child of <svg>; DigiCert
    // rejects logos (invalid_vmc_logo) when anything else precedes it.
    const titleWasFirst = getFirstChildElementName(innerContent) === 'title';
    const hoisted = hoistTitleAndDesc(innerContent);
    innerContent = hoisted.body;

    const titleElement = hoisted.title || '<title>Logo</title>';
    const descElement = hoisted.desc || '<desc>BIMI compliant logo</desc>';

    if (!hoisted.title) {
      result.fixesApplied.push('Added <title> element');
    } else if (!titleWasFirst) {
      result.fixesApplied.push('Moved <title> to first child of <svg> (SVG Tiny PS requirement)');
    }
    if (!hoisted.desc) {
      result.fixesApplied.push('Added <desc> element');
    }
    if (hoisted.duplicatesRemoved > 0) {
      result.fixesApplied.push(
        `Removed ${hoisted.duplicatesRemoved} duplicate <title>/<desc> element(s)`
      );
    }

    // Step 9: Reconstruct the SVG - title first, desc second, content otherwise UNTOUCHED
    const finalSvg = `<?xml version="1.0"?>\n<svg ${newSvgAttrs}>\n  ${titleElement}\n  ${descElement}${innerContent}\n</svg>`;

    // Step 10: Validate
    const fileSize = new TextEncoder().encode(finalSvg).length;
    if (fileSize > 32 * 1024) {
      result.errors.push(`SVG exceeds 32KB limit (${Math.round(fileSize / 1024)}KB)`);
    }

    // Check for prohibited elements (just warn, don't remove)
    if (/<script[\s>]/i.test(innerContent)) {
      result.warnings.push('Contains <script> element - may not be BIMI compliant');
    }
    if (/<image[\s>]/i.test(innerContent)) {
      result.warnings.push('Contains <image> element - may not be BIMI compliant');
    }
    // Check if there are still style elements (shouldn't be after inlining)
    if (/<style[\s>]/i.test(innerContent)) {
      result.warnings.push(
        'Contains <style> element that could not be fully inlined - may not be BIMI compliant'
      );
    }

    result.fixedSvg = finalSvg;
    result.isValid = result.errors.length === 0;
    result.fileSize = fileSize;

    return result;
  } catch (error) {
    result.errors.push(`Conversion error: ${error.message}`);
    return result;
  }
}

/**
 * Parse viewBox from SVG attributes
 */
function parseViewBox(attrs) {
  const match = attrs.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (!match) return null;

  const parts = match[1]
    .trim()
    .split(/[\s,]+/)
    .map(parseFloat);
  if (parts.length !== 4 || parts.some(isNaN)) return null;

  return {
    x: parts[0],
    y: parts[1],
    width: parts[2],
    height: parts[3],
  };
}

/**
 * Quick check if SVG is already BIMI compliant
 */
export function checkBimiCompliance(svgContent) {
  const issues = [];

  if (!svgContent || typeof svgContent !== 'string') {
    return { isCompliant: false, issues: ['Invalid SVG content'] };
  }

  if (!svgContent.trim().startsWith('<?xml')) {
    issues.push('Missing XML declaration');
  }

  const svgMatch = svgContent.match(/<svg([^>]*)>/i);
  if (!svgMatch) {
    return { isCompliant: false, issues: ['Missing <svg> tag'] };
  }

  const attrs = svgMatch[1];

  if (!/xmlns\s*=\s*["']http:\/\/www\.w3\.org\/2000\/svg["']/i.test(attrs)) {
    issues.push('Missing xmlns attribute');
  }
  if (!/baseProfile\s*=\s*["']tiny-ps["']/i.test(attrs)) {
    issues.push('Missing baseProfile="tiny-ps"');
  }
  if (!/version\s*=\s*["']1\.2["']/i.test(attrs)) {
    issues.push('Missing version="1.2"');
  }

  const viewBoxMatch = attrs.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (!viewBoxMatch) {
    issues.push('Missing viewBox');
  } else {
    const parts = viewBoxMatch[1]
      .trim()
      .split(/[\s,]+/)
      .map(parseFloat);
    if (parts.length === 4 && Math.abs(parts[2] - parts[3]) > 0.01) {
      issues.push('viewBox is not square');
    }
  }

  if (!/<title[\s/>]/i.test(svgContent)) {
    issues.push('Missing <title>');
  } else {
    // <title> must be the FIRST child element of <svg> — DigiCert rejects otherwise
    const afterSvgTag = svgContent.slice(svgMatch.index + svgMatch[0].length);
    const firstChild = afterSvgTag.replace(/<!--[\s\S]*?-->/g, '').match(/<\s*([a-zA-Z][\w-]*)/);
    if (!firstChild || firstChild[1].toLowerCase() !== 'title') {
      issues.push('<title> must be the first child of <svg> (SVG Tiny PS requirement)');
    }
  }
  if (!/<desc[\s/>]/i.test(svgContent)) {
    issues.push('Missing <desc>');
  }

  // Check for prohibited elements (BIMI SVG Tiny 1.2 PS)
  if (/<style[\s>]/i.test(svgContent)) {
    issues.push('Contains <style> element - not allowed in BIMI SVG');
  }
  if (/<script[\s>]/i.test(svgContent)) {
    issues.push('Contains <script> element - not allowed in BIMI SVG');
  }
  if (/<image[\s>]/i.test(svgContent)) {
    issues.push('Contains <image> element - not allowed in BIMI SVG');
  }
  if (/<linearGradient[\s>]/i.test(svgContent)) {
    issues.push('Contains <linearGradient> - not allowed in BIMI SVG P/S profile');
  }
  if (/<radialGradient[\s>]/i.test(svgContent)) {
    issues.push('Contains <radialGradient> - not allowed in BIMI SVG P/S profile');
  }
  // clipPath and related - NOT allowed in SVG Tiny 1.2 PS
  if (/<clipPath[\s>]/i.test(svgContent)) {
    issues.push('Contains <clipPath> element - not allowed in BIMI SVG Tiny PS');
  }
  if (/\sclip-path\s*=\s*["']/i.test(svgContent)) {
    issues.push('Contains clip-path attribute - not allowed in BIMI SVG Tiny PS');
  }
  // mask and filter - NOT allowed
  if (/<mask[\s>]/i.test(svgContent)) {
    issues.push('Contains <mask> element - not allowed in BIMI SVG');
  }
  if (/<filter[\s>]/i.test(svgContent)) {
    issues.push('Contains <filter> element - not allowed in BIMI SVG');
  }
  if (/\sfilter\s*=\s*["']/i.test(svgContent)) {
    issues.push('Contains filter attribute - not allowed in BIMI SVG');
  }
  // pattern - NOT allowed
  if (/<pattern[\s>]/i.test(svgContent)) {
    issues.push('Contains <pattern> element - not allowed in BIMI SVG');
  }
  // foreignObject - NOT allowed
  if (/<foreignObject[\s>]/i.test(svgContent)) {
    issues.push('Contains <foreignObject> element - not allowed in BIMI SVG');
  }
  // Animation elements - NOT allowed
  if (/<animate[\s>]/i.test(svgContent)) {
    issues.push('Contains animation elements - not allowed in BIMI SVG');
  }
  if (/<animateTransform[\s>]/i.test(svgContent)) {
    issues.push('Contains animation elements - not allowed in BIMI SVG');
  }
  if (/<set[\s>]/i.test(svgContent)) {
    issues.push('Contains <set> animation element - not allowed in BIMI SVG');
  }
  // Check for defs containing only forbidden content (empty or only whitespace after cleanup)
  const defsMatch = svgContent.match(/<defs[^>]*>([\s\S]*?)<\/defs>/i);
  if (defsMatch) {
    const defsContent = defsMatch[1].trim();
    // If defs contains clipPath, mask, filter, pattern - flag it
    if (/<clipPath[\s>]/i.test(defsContent)) {
      issues.push('Contains <defs> with clipPath - clipPath not allowed in BIMI SVG');
    }
  }

  const fileSize = new TextEncoder().encode(svgContent).length;
  if (fileSize > 32 * 1024) {
    issues.push('Exceeds 32KB limit');
  }

  return {
    isCompliant: issues.length === 0,
    issues,
    fileSize,
  };
}

/**
 * Inline CSS styles from <style> elements into element attributes
 * BIMI SVG Tiny 1.2 PS does not allow <style> elements
 * @param {string} content - SVG inner content
 * @returns {Object} Result with modified content
 */
function inlineCssStyles(content) {
  const result = {
    content,
    modified: false,
    removedStyles: 0,
  };

  // Check if there are any style elements
  if (!/<style[\s>]/i.test(content)) {
    return result;
  }

  let modifiedContent = content;

  // Extract all style blocks
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  const styleMatches = [...content.matchAll(styleRegex)];

  if (styleMatches.length === 0) {
    return result;
  }

  // Parse CSS rules from all style blocks
  const cssRules = new Map();

  for (const match of styleMatches) {
    const cssContent = match[1];
    // Parse simple CSS rules: .className { property: value; }
    const ruleRegex = /\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g;
    let ruleMatch;

    while ((ruleMatch = ruleRegex.exec(cssContent)) !== null) {
      const className = ruleMatch[1];
      const properties = ruleMatch[2].trim();

      // Parse properties into an object
      const propsMap = {};
      const propRegex = /([a-zA-Z-]+)\s*:\s*([^;]+);?/g;
      let propMatch;

      while ((propMatch = propRegex.exec(properties)) !== null) {
        const propName = propMatch[1].trim();
        const propValue = propMatch[2].trim();
        propsMap[propName] = propValue;
      }

      cssRules.set(className, propsMap);
    }
  }

  // Apply CSS rules to elements with matching classes
  for (const [className, properties] of cssRules) {
    // Skip if no properties to apply
    if (Object.keys(properties).length === 0) continue;

    // Find elements with this class and add inline attributes
    // Regex captures: (tag start with class attr)(remaining attrs)(closing > or />)
    const classPattern = new RegExp(
      `(<[a-zA-Z][\\w:-]*(?:\\s+[^>]*)?\\sclass\\s*=\\s*["'][^"']*\\b${className}\\b[^"']*["'])([^>]*?)(\\/?>)`,
      'gi'
    );

    modifiedContent = modifiedContent.replace(
      classPattern,
      (match, tagWithClass, restOfTag, closing) => {
        // Build attributes to add, avoiding duplicates
        const fullTag = tagWithClass + restOfTag;
        let attrsToAdd = '';

        for (const [prop, value] of Object.entries(properties)) {
          const svgAttr = cssPropertyToSvgAttribute(prop);
          if (svgAttr) {
            // Check if this attribute already exists on the element
            const existingAttrRegex = new RegExp(`\\s${svgAttr}\\s*=\\s*["']`, 'i');
            if (!existingAttrRegex.test(fullTag)) {
              attrsToAdd += ` ${svgAttr}="${value}"`;
            }
          }
        }

        return tagWithClass + restOfTag + attrsToAdd + closing;
      }
    );
  }

  // Remove <style> elements
  modifiedContent = modifiedContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  result.removedStyles = styleMatches.length;

  // Remove class attributes (now useless after inlining, and some validators flag them)
  modifiedContent = modifiedContent.replace(/\s+class\s*=\s*["'][^"']*["']/gi, '');

  // Remove data-* attributes (NOT allowed in SVG Tiny 1.2)
  modifiedContent = modifiedContent.replace(/\s+data-[a-zA-Z0-9-]+\s*=\s*["'][^"']*["']/gi, '');

  // Remove no-op transforms like transform="translate(0 0)" or transform="translate(0, 0)"
  modifiedContent = modifiedContent.replace(
    /\s+transform\s*=\s*["']translate\s*\(\s*0\s*[,\s]\s*0\s*\)["']/gi,
    ''
  );

  // Clean up empty <defs> if all content was removed
  modifiedContent = modifiedContent.replace(/<defs[^>]*>\s*<\/defs>/gi, '');

  result.content = modifiedContent;
  result.modified = true;

  return result;
}

/**
 * Convert CSS property name to SVG attribute name
 * @param {string} cssProp - CSS property name
 * @returns {string|null} SVG attribute name or null if not mappable
 */
function cssPropertyToSvgAttribute(cssProp) {
  // Most SVG presentation attributes have the same name as CSS properties
  const directMappings = [
    'fill',
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-miterlimit',
    'stroke-dasharray',
    'stroke-dashoffset',
    'stroke-opacity',
    'fill-opacity',
    'fill-rule',
    'opacity',
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'text-anchor',
    'text-decoration',
    'letter-spacing',
    'word-spacing',
    'dominant-baseline',
    'alignment-baseline',
    'clip-path',
    'clip-rule',
    'color',
    'display',
    'visibility',
    'overflow',
    'pointer-events',
  ];

  if (directMappings.includes(cssProp)) {
    return cssProp;
  }

  // Handle special cases
  const specialMappings = {
    'background-color': null, // SVG doesn't have background-color
    background: null,
    border: null,
    margin: null,
    padding: null,
  };

  if (cssProp in specialMappings) {
    return specialMappings[cssProp];
  }

  // Default: try to use the property as-is (many CSS properties work in SVG)
  return cssProp;
}

/**
 * Replace gradients with solid colors
 * linearGradient and radialGradient are NOT allowed in BIMI SVG Tiny P/S
 * @param {string} content - SVG inner content
 * @returns {Object} Result with modified content
 */
function replaceGradientsWithSolidColors(content) {
  const result = {
    content,
    modified: false,
    gradientsReplaced: 0,
  };

  // Check if there are any gradients
  if (!/<(?:linear|radial)Gradient[\s>]/i.test(content)) {
    return result;
  }

  let modifiedContent = content;

  // Find all gradient definitions and extract their IDs and colors
  const gradientRegex =
    /<(linear|radial)Gradient[^>]*\sid\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/\1Gradient>/gi;
  const gradients = new Map();

  let gradientMatch;
  while ((gradientMatch = gradientRegex.exec(content)) !== null) {
    const gradientId = gradientMatch[2];
    const gradientContent = gradientMatch[3];

    // Extract colors from stop elements
    const stopRegex = /stop-color\s*[=:]\s*["']?([^"';\s>]+)/gi;
    const colors = [];
    let stopMatch;

    while ((stopMatch = stopRegex.exec(gradientContent)) !== null) {
      colors.push(stopMatch[1]);
    }

    // Also check for stop color as attribute
    const stopAttrRegex = /<stop[^>]*stop-color\s*=\s*["']([^"']+)["']/gi;
    while ((stopMatch = stopAttrRegex.exec(gradientContent)) !== null) {
      if (!colors.includes(stopMatch[1])) {
        colors.push(stopMatch[1]);
      }
    }

    if (colors.length > 0) {
      // Use the last color (typically the dominant/end color) or average
      // For BIMI, using a representative solid color is acceptable
      gradients.set(gradientId, colors[colors.length - 1]);
    }
  }

  // Replace fill="url(#gradient-id)" and stroke="url(#gradient-id)" with solid colors
  for (const [gradientId, solidColor] of gradients) {
    const urlPattern = new RegExp(
      `(fill|stroke)\\s*=\\s*["']url\\s*\\(\\s*#${gradientId}\\s*\\)["']`,
      'gi'
    );

    modifiedContent = modifiedContent.replace(urlPattern, `$1="${solidColor}"`);
    result.gradientsReplaced++;
  }

  // Remove gradient definitions from defs
  modifiedContent = modifiedContent.replace(
    /<(linear|radial)Gradient[^>]*>[\s\S]*?<\/\1Gradient>/gi,
    ''
  );

  // Clean up empty defs
  modifiedContent = modifiedContent.replace(/<defs[^>]*>\s*<\/defs>/gi, '');

  // Also remove defs that only contain whitespace after gradient removal
  modifiedContent = modifiedContent.replace(/<defs[^>]*>[\s\n\r]*<\/defs>/gi, '');

  if (modifiedContent !== content) {
    result.content = modifiedContent;
    result.modified = true;
  }

  return result;
}

/**
 * Remove clipPath elements and clip-path attributes
 * clipPath is NOT allowed in SVG Tiny 1.2 PS (BIMI profile)
 * @param {string} content - SVG inner content
 * @returns {Object} Result with modified content
 */
function removeClipPaths(content) {
  const result = {
    content,
    modified: false,
    clipPathsRemoved: 0,
  };

  let modifiedContent = content;

  // Count clipPath elements before removal
  const clipPathMatches = content.match(/<clipPath[\s>]/gi);
  if (clipPathMatches) {
    result.clipPathsRemoved = clipPathMatches.length;
  }

  // Remove clipPath elements (including self-closing and with content)
  modifiedContent = modifiedContent.replace(/<clipPath[^>]*>[\s\S]*?<\/clipPath>/gi, '');
  modifiedContent = modifiedContent.replace(/<clipPath[^>]*\/>/gi, '');

  // Remove clip-path attributes from all elements
  modifiedContent = modifiedContent.replace(/\s+clip-path\s*=\s*["'][^"']*["']/gi, '');

  // Also remove clip-rule attributes (only useful with clip-path)
  modifiedContent = modifiedContent.replace(/\s+clip-rule\s*=\s*["'][^"']*["']/gi, '');

  // Clean up empty defs elements that may have been left behind
  modifiedContent = modifiedContent.replace(/<defs[^>]*>\s*<\/defs>/gi, '');
  modifiedContent = modifiedContent.replace(/<defs[^>]*>[\s\n\r]*<\/defs>/gi, '');

  if (modifiedContent !== content) {
    result.content = modifiedContent;
    result.modified = true;
  }

  return result;
}

/**
 * Remove other forbidden elements in BIMI SVG Tiny 1.2 PS
 * @param {string} content - SVG inner content
 * @returns {Object} Result with modified content
 */
function removeForbiddenElements(content) {
  const result = {
    content,
    modified: false,
    fixes: [],
    warnings: [],
  };

  let modifiedContent = content;

  // List of forbidden elements in SVG Tiny 1.2 PS
  const forbiddenElements = [
    { name: 'mask', description: 'mask element' },
    { name: 'filter', description: 'filter element' },
    { name: 'pattern', description: 'pattern element' },
    { name: 'symbol', description: 'symbol element' },
    { name: 'marker', description: 'marker element' },
    { name: 'foreignObject', description: 'foreignObject element' },
    { name: 'switch', description: 'switch element' },
    { name: 'a', description: 'anchor/link element' },
    // Animation elements
    { name: 'animate', description: 'animate element' },
    { name: 'animateTransform', description: 'animateTransform element' },
    { name: 'animateMotion', description: 'animateMotion element' },
    { name: 'animateColor', description: 'animateColor element' },
    { name: 'set', description: 'set (animation) element' },
  ];

  for (const element of forbiddenElements) {
    const regex = new RegExp(`<${element.name}[\\s>]`, 'gi');
    if (regex.test(modifiedContent)) {
      // Remove the element (both self-closing and with content)
      const removeRegex = new RegExp(
        `<${element.name}[^>]*>[\\s\\S]*?<\\/${element.name}>|<${element.name}[^>]*\\/>`,
        'gi'
      );
      modifiedContent = modifiedContent.replace(removeRegex, '');
      result.fixes.push(`Removed ${element.description} (not allowed in BIMI SVG)`);
      result.warnings.push(
        `Removed ${element.description} - this may affect your logo appearance.`
      );
    }
  }

  // Remove mask attribute references
  if (/\s+mask\s*=\s*["'][^"']*["']/i.test(modifiedContent)) {
    modifiedContent = modifiedContent.replace(/\s+mask\s*=\s*["'][^"']*["']/gi, '');
    result.fixes.push('Removed mask attribute references');
  }

  // Remove filter attribute references
  if (/\s+filter\s*=\s*["'][^"']*["']/i.test(modifiedContent)) {
    modifiedContent = modifiedContent.replace(/\s+filter\s*=\s*["'][^"']*["']/gi, '');
    result.fixes.push('Removed filter attribute references');
  }

  // Remove marker attributes
  const markerAttrs = ['marker-start', 'marker-mid', 'marker-end', 'marker'];
  for (const attr of markerAttrs) {
    const attrRegex = new RegExp(`\\s+${attr}\\s*=\\s*["'][^"']*["']`, 'gi');
    if (attrRegex.test(modifiedContent)) {
      modifiedContent = modifiedContent.replace(attrRegex, '');
      result.fixes.push(`Removed ${attr} attribute`);
    }
  }

  // Clean up empty defs
  modifiedContent = modifiedContent.replace(/<defs[^>]*>\s*<\/defs>/gi, '');
  modifiedContent = modifiedContent.replace(/<defs[^>]*>[\s\n\r]*<\/defs>/gi, '');

  if (modifiedContent !== content) {
    result.content = modifiedContent;
    result.modified = true;
  }

  return result;
}

/**
 * Apply edge shape to BIMI SVG using a frame overlay approach
 * Since BIMI doesn't allow clipPath, we add a foreground "frame" that covers
 * areas outside the desired shape (like a physical mask overlay)
 * @param {string} svgContent - BIMI-compliant SVG content
 * @param {'square' | 'rounded' | 'circle'} shape - Edge shape to apply
 * @param {string} frameColor - Color for the frame overlay (default: white)
 * @returns {string} Modified SVG with edge shape frame applied
 */
export function applyEdgeShape(svgContent, shape = 'square', frameColor = 'white') {
  // Square shape requires no modification
  if (shape === 'square' || !svgContent) {
    return svgContent;
  }

  try {
    // Extract viewBox to get dimensions
    const viewBoxMatch = svgContent.match(/viewBox\s*=\s*["']([^"']+)["']/i);
    if (!viewBoxMatch) {
      console.warn('applyEdgeShape: No viewBox found, returning original SVG');
      return svgContent;
    }

    const viewBoxParts = viewBoxMatch[1]
      .trim()
      .split(/[\s,]+/)
      .map(parseFloat);
    if (viewBoxParts.length !== 4 || viewBoxParts.some(isNaN)) {
      console.warn('applyEdgeShape: Invalid viewBox, returning original SVG');
      return svgContent;
    }

    const [minX, minY, width, height] = viewBoxParts;

    // Detect actual content bounds from background rect (for non-square source SVGs)
    // This ensures the shape is inscribed in the actual content, not the expanded viewBox
    const contentBounds = detectContentBounds(svgContent, { minX, minY, width, height });

    // Use content bounds if found, otherwise fall back to viewBox
    const shapeBounds = contentBounds || { x: minX, y: minY, width, height };
    const shapeX = contentBounds ? contentBounds.x : minX;
    const shapeY = contentBounds ? contentBounds.y : minY;
    const shapeW = shapeBounds.width;
    const shapeH = shapeBounds.height;

    // Calculate shape center and radius based on content bounds
    const centerX = shapeX + shapeW / 2;
    const centerY = shapeY + shapeH / 2;
    const radius = Math.min(shapeW, shapeH) / 2;

    // Create frame overlay path using fill-rule="evenodd"
    // This draws an outer rectangle and an inner shape - evenodd creates the "frame" effect
    // OUTER rect extends BEYOND viewBox to ensure complete edge coverage (no anti-aliasing gaps)
    // INNER cutout uses content bounds (may be smaller than viewBox for non-square sources)
    const margin = Math.min(width, height) * 0.02; // 2% margin beyond viewBox for outer rect
    let framePath = '';

    if (shape === 'circle') {
      // Frame: outer rectangle (extended beyond viewBox) with circular cutout (at content bounds)
      const outerRect = `M ${minX - margin} ${minY - margin} L ${minX + width + margin} ${minY - margin} L ${minX + width + margin} ${minY + height + margin} L ${minX - margin} ${minY + height + margin} Z`;
      // Circle inscribed in content bounds (not viewBox)
      const circleTop = centerY - radius;
      const circleBottom = centerY + radius;
      const innerCircle = `M ${centerX} ${circleTop} A ${radius} ${radius} 0 1 0 ${centerX} ${circleBottom} A ${radius} ${radius} 0 1 0 ${centerX} ${circleTop}`;
      framePath = `<path d="${outerRect} ${innerCircle}" fill="${frameColor}" fill-rule="evenodd" stroke="none"/>`;
    } else if (shape === 'rounded') {
      // Frame: outer rectangle (extended beyond viewBox) with rounded rectangle cutout (at content bounds)
      const cornerRadius = Math.min(shapeW, shapeH) * 0.15;
      const outerRect = `M ${minX - margin} ${minY - margin} L ${minX + width + margin} ${minY - margin} L ${minX + width + margin} ${minY + height + margin} L ${minX - margin} ${minY + height + margin} Z`;
      // Rounded rect at content bounds
      const r = cornerRadius;
      const x = shapeX;
      const y = shapeY;
      const w = shapeW;
      const h = shapeH;
      // Start at top-left after corner, go clockwise
      const innerRounded =
        `M ${x + r} ${y} ` +
        `L ${x + w - r} ${y} ` +
        `A ${r} ${r} 0 0 1 ${x + w} ${y + r} ` +
        `L ${x + w} ${y + h - r} ` +
        `A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} ` +
        `L ${x + r} ${y + h} ` +
        `A ${r} ${r} 0 0 1 ${x} ${y + h - r} ` +
        `L ${x} ${y + r} ` +
        `A ${r} ${r} 0 0 1 ${x + r} ${y} Z`;
      framePath = `<path d="${outerRect} ${innerRounded}" fill="${frameColor}" fill-rule="evenodd" stroke="none"/>`;
    }

    if (!framePath) {
      return svgContent;
    }

    // Insert frame BEFORE closing </svg> tag so it renders ON TOP of content
    const closingSvgMatch = svgContent.match(/<\/svg>\s*$/i);
    if (!closingSvgMatch) {
      console.warn('applyEdgeShape: No closing </svg> tag found');
      return svgContent;
    }

    const insertPosition = closingSvgMatch.index;
    const before = svgContent.substring(0, insertPosition);
    const after = svgContent.substring(insertPosition);

    return `${before}\n  <!-- Edge shape frame overlay -->\n  ${framePath}\n${after}`;
  } catch (error) {
    console.error('applyEdgeShape error:', error);
    return svgContent;
  }
}

/**
 * Detect content bounds from the first background rect in SVG content
 * Used to properly inscribe edge shapes for non-square source SVGs
 * @param {string} svgContent - Full SVG content
 * @param {Object} viewBox - ViewBox dimensions {minX, minY, width, height}
 * @returns {Object|null} Content bounds {x, y, width, height} or null if not found
 */
function detectContentBounds(svgContent, viewBox) {
  // Find the first rect element - likely the background
  const rectMatch = svgContent.match(/<rect([^>]*)\/?>/i);
  if (!rectMatch) {
    return null;
  }

  const rectAttrs = rectMatch[1];

  // Extract rect dimensions
  const widthMatch = rectAttrs.match(/\bwidth\s*=\s*["']([^"']+)["']/i);
  const heightMatch = rectAttrs.match(/\bheight\s*=\s*["']([^"']+)["']/i);

  if (!widthMatch || !heightMatch) {
    return null;
  }

  const rectWidth = parseFloat(widthMatch[1]);
  const rectHeight = parseFloat(heightMatch[1]);

  // Check if this rect is large enough to be a background (at least 50% of viewBox)
  const minDim = Math.min(viewBox.width, viewBox.height);
  if (rectWidth < minDim * 0.5 || rectHeight < minDim * 0.5) {
    return null; // Too small, not a background rect
  }

  // Extract x and y (default to 0 if not specified)
  const xMatch = rectAttrs.match(/\bx\s*=\s*["']([^"']+)["']/i);
  const yMatch = rectAttrs.match(/\by\s*=\s*["']([^"']+)["']/i);

  const rectX = xMatch ? parseFloat(xMatch[1]) : 0;
  const rectY = yMatch ? parseFloat(yMatch[1]) : 0;

  return {
    x: rectX,
    y: rectY,
    width: rectWidth,
    height: rectHeight,
  };
}
